"use client";

import type { PublicHelpSpace } from "@v1/backend/logic/pages/public_help_space_groups";
import { cn } from "@v1/ui/utils";
import { Home } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useHelpHref } from "./help-link-base";
import { type HelpNavExpansion, HelpNavSpaceItem } from "./help-nav-rows";
import type { HelpSpaTarget } from "./help-spa-path";
import { buildHelpSpaceTree, resolveHelpNavPath } from "./help-tree";

/** Sidebar: space › page › subpage. Open = ancestors of the current target + reader's unfolds. */
export function HelpNavTree({
  spaces,
  org_slug,
  current,
}: {
  readonly spaces: readonly PublicHelpSpace[];
  readonly org_slug: string;
  /** Null when the path is unknown; treated like the start page. */
  readonly current: HelpSpaTarget | null;
}): ReactElement {
  const t = useTranslations("public.helpNavTree");
  const helpHref = useHelpHref(org_slug);
  const trees = useMemo(
    () =>
      spaces
        .filter((space) => space.pages.length > 0)
        .map((space) => ({ space, tree: buildHelpSpaceTree(space.pages) })),
    [spaces],
  );
  const path = useMemo(
    () => resolveHelpNavPath(spaces, current),
    [spaces, current],
  );
  const [manual, setManual] = useState<Readonly<Record<string, boolean>>>({});

  // Navigating re-opens the new path, even where the reader folded it.
  useEffect(() => {
    setManual((previous) => {
      const entries = Object.entries(previous);
      const kept = entries.filter(([key]) => !path.pathKeys.has(key));
      return kept.length === entries.length
        ? previous
        : Object.fromEntries(kept);
    });
  }, [path]);

  const expansion = useMemo<HelpNavExpansion>(
    () => ({
      isOpen: (key, auto) => manual[key] ?? auto,
      toggle: (key, open) =>
        setManual((previous) => ({ ...previous, [key]: !open })),
    }),
    [manual],
  );

  const homeCurrent = current === null || current.kind === "center";

  return (
    <nav aria-label={t("wissensnavigation")} className="text-[14px]">
      <Link
        href={helpHref({ kind: "center" })}
        prefetch={false}
        aria-current={homeCurrent ? "page" : undefined}
        className={cn(
          "flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-[14.5px] font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-action-500/30",
          homeCurrent
            ? "bg-action-500/[0.08] text-action-700"
            : "text-primary/75 hover:bg-muted/60 hover:text-primary",
        )}
      >
        <Home
          aria-hidden
          className="h-[17px] w-[17px] shrink-0"
          strokeWidth={1.75}
        />
        {t("startseite")}
      </Link>

      <div className="mx-1 my-5 border-t border-border/60" />
      <ul className="space-y-1">
        {trees.map(({ space, tree }) => (
          <HelpNavSpaceItem
            key={space.space_id}
            org_slug={org_slug}
            space={space}
            tree={tree}
            path={path}
            expansion={expansion}
          />
        ))}
      </ul>
    </nav>
  );
}
